import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { ROUTES } from '../../utils/constants';
import SearchBar from '../search/SearchBar';

// NEW / 必看 徽章：與站內綠色語彙一致，但收斂成低調的 ring pill
const Badge = ({ label }: { label: string }) => (
    <span className={`shrink-0 text-[10px] leading-none font-bold px-1.5 py-1 rounded-md ring-1 ${label === 'NEW'
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/70'
        : 'bg-amber-50 text-amber-700 ring-amber-200/70'
        }`}>
        {label}
    </span>
);

const GameIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const Header = () => {
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    const isActive = (path: string) => location.pathname === path;

    type DropdownItem = {
        path: string;
        label: string;
        description: string;
        badge?: string;
    };

    const navigation: Array<
        | { path: string; label: string; type: 'link' }
        | { label: string; type: 'dropdown'; items: DropdownItem[] }
    > = [
            { path: ROUTES.COURTS, label: '找球場', type: 'link' },
            { path: ROUTES.TOURNAMENTS, label: '賽事', type: 'link' },
            { path: ROUTES.AEPL, label: 'AEPL 職業聯賽', type: 'link' },
            {
                label: '學習',
                type: 'dropdown',
                items: [
                    { path: ROUTES.TECHNIQUES, label: '技巧百科', description: '12+ 深度教學含步驟分解' },
                    { path: ROUTES.TRAINING_PROGRAMS, label: '訓練菜單', description: '8 套系統化週日進度', badge: 'NEW' },
                    { path: ROUTES.PLAYBOOK, label: '戰術劇本庫', description: '30+ 情境戰術對照', badge: 'NEW' },
                    { path: ROUTES.ARTICLES, label: '深度專欄', description: '器材評測、運動科學、族群指南' },
                    { path: ROUTES.NEWCOMER_GUIDE, label: '新手懶人包', description: '費用、轉項分析、入門指引', badge: '必看' },
                    { path: ROUTES.RULES, label: '規則教學', description: '3D 互動式規則解說' },
                    { path: ROUTES.LEARNING_PATHS, label: '學習路徑', description: '從入門到精通的系統化課程' },
                    { path: ROUTES.GLOSSARY, label: '術語字典', description: '中英對照術語大全' },
                    { path: ROUTES.FAQ, label: '常見問題', description: '解決你的所有疑問' },
                ],
            },
            {
                label: '裝備選手',
                type: 'dropdown',
                items: [
                    { path: ROUTES.EQUIPMENT, label: '裝備指南', description: '球拍材質與選購建議' },
                    { path: ROUTES.PADDLE_DATABASE, label: '球拍資料庫', description: '40+ 款球拍規格完整對照', badge: 'NEW' },
                    { path: ROUTES.PRO_PLAYERS, label: '頂尖選手', description: '25+ 位世界冠軍資料庫' },
                    { path: ROUTES.RATINGS, label: 'DUPR 評級指南', description: '2026 全球通用評分系統' },
                    { path: ROUTES.HISTORY, label: '匹克球編年史', description: '1965-2026 大事記' },
                    { path: ROUTES.HALL_OF_FAME, label: '名人堂', description: '創辦人、傳奇、台灣推廣者', badge: 'NEW' },
                    { path: ROUTES.VIDEOS, label: '教學影片中心', description: '20+ 國際名師精選' },
                ],
            },
            {
                label: '工具',
                type: 'dropdown',
                items: [
                    { path: ROUTES.TOOLS, label: '工具總覽', description: '所有實用工具一覽', badge: 'NEW' },
                    { path: ROUTES.TOOL_DUPR, label: 'DUPR 模擬器', description: '預估下一場的評分變動' },
                    { path: ROUTES.TOOL_ROTATION, label: '輪轉排程器', description: '約球輪次自動排程' },
                    { path: ROUTES.TOOL_BRACKET, label: '籤表產生器', description: '單淘汰/循環賽籤表' },
                    { path: ROUTES.TOOL_COURT_LINES, label: '場地劃線指南', description: '標準尺寸與改造教學' },
                    { path: ROUTES.SCORER, label: '比賽計分器', description: '單打/雙打即時計分' },
                ],
            },
            {
                label: '更多',
                type: 'dropdown',
                items: [
                    { path: ROUTES.RESOURCES, label: '資源中心', description: '影片、社團與書籍推薦' },
                    { path: ROUTES.ABOUT, label: '關於我們', description: 'Picklemaster 的故事' },
                ],
            },
        ];

    return (
        <header className="sticky top-0 z-50 shadow-lg bg-white">
            {/* Layered Background with Pickleball Court Pattern (now atop solid bg-white to prevent bleed-through on sticky scroll) */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50">
                {/* Subtle court grid pattern */}
                <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: `
              linear-gradient(90deg, #10b981 1px, transparent 1px),
              linear-gradient(#10b981 1px, transparent 1px)
            `,
                        backgroundSize: '40px 40px'
                    }}
                />
                {/* Court center line accent */}
                <div className="absolute left-0 right-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
            </div>

            {/* Border */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />

            <nav className="container mx-auto px-6 py-3 relative">
                <div className="flex items-center justify-between">
                    {/* Modern Sports Brand Logo */}
                    <Link
                        to={ROUTES.HOME}
                        className="flex items-center space-x-3 group relative py-1"
                    >
                        {/* Logo with dynamic glow */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-emerald-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <img
                                src="/logo.webp?v=2"
                                alt="Picklemaster Taiwan"
                                className="w-14 h-14 md:w-16 md:h-16 object-contain relative z-10 transition-transform duration-300 group-hover:scale-110 drop-shadow-lg"
                            />
                        </div>

                        {/* Modern Athletic Brand Name */}
                        <div className="flex flex-col">
                            {/* Main Brand - Bold & Modern */}
                            <div className="flex items-baseline space-x-2">
                                <h1 className="text-xl font-black tracking-tight leading-none" style={{ fontFamily: "'Inter', sans-serif" }}>
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                                        PICKLEMASTER
                                    </span>
                                </h1>
                                {/* Dynamic accent bar */}
                                <div className="flex flex-col space-y-0.5 hidden sm:flex">
                                    <div className="h-0.5 w-8 bg-gradient-to-r from-emerald-500 to-transparent rounded-full" />
                                    <div className="h-0.5 w-5 bg-gradient-to-r from-teal-500 to-transparent rounded-full" />
                                </div>
                            </div>
                            {/* Subtitle - Sleek & International */}
                            <div className="flex items-center space-x-2 mt-0.5">
                                <p className="text-[9px] font-bold text-emerald-600/80 uppercase tracking-[0.15em]" style={{ fontFamily: "'Inter', sans-serif" }}>
                                    Taiwan Pickleball
                                </p>
                            </div>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden xl:flex items-center gap-0.5">
                        {/* Search Bar */}
                        <div className="mr-2 shrink-0">
                            <SearchBar variant="header" />
                        </div>

                        {navigation.map((item, index) => (
                            <div
                                key={index}
                                className="relative shrink-0"
                                onMouseEnter={() => item.type === 'dropdown' && setActiveDropdown(item.label)}
                                onMouseLeave={() => item.type === 'dropdown' && setActiveDropdown(null)}
                            >
                                {item.type === 'link' ? (
                                    <Link
                                        to={item.path!}
                                        className={`
                      px-3.5 py-2 rounded-xl font-semibold transition-colors duration-200 block text-sm relative whitespace-nowrap
                      ${isActive(item.path!)
                                                ? 'text-emerald-700 bg-emerald-50'
                                                : 'text-neutral-700 hover:text-emerald-700 hover:bg-neutral-100/70'
                                            }
                    `}
                                    >
                                        {item.label}
                                        {isActive(item.path!) && (
                                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-transparent via-emerald-500 to-transparent rounded-full" />
                                        )}
                                    </Link>
                                ) : (
                                    <>
                                        <button
                                            className={`px-3.5 py-2 rounded-xl font-semibold transition-colors duration-200 flex items-center gap-1.5 text-sm whitespace-nowrap ${activeDropdown === item.label
                                                ? 'text-emerald-700 bg-neutral-100/70'
                                                : 'text-neutral-700 hover:text-emerald-700 hover:bg-neutral-100/70'
                                                }`}
                                            aria-expanded={activeDropdown === item.label}
                                            aria-haspopup="true"
                                            onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                                        >
                                            <span>{item.label}</span>
                                            <svg
                                                className="w-3.5 h-3.5 transition-transform duration-200"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                style={{ transform: activeDropdown === item.label ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Dropdown Menu — 項目多的選單改雙欄，避免長到掉出畫面 */}
                                        {activeDropdown === item.label && (
                                            <div
                                                className={`absolute top-full right-0 pt-2 ${item.items.length > 6 ? 'w-[544px]' : 'w-80'}`}
                                            >
                                                <div className="animate-slide-down origin-top bg-white/98 backdrop-blur-xl rounded-2xl shadow-xl shadow-neutral-900/10 ring-1 ring-neutral-900/5 p-2">
                                                    <div className={item.items.length > 6 ? 'grid grid-cols-2 gap-0.5' : 'space-y-0.5'}>
                                                        {item.items.map((subItem, subIndex) => (
                                                            <Link
                                                                key={subIndex}
                                                                to={subItem.path}
                                                                className="group/item block rounded-xl px-3 py-2.5 hover:bg-emerald-50/70 transition-colors"
                                                                onClick={() => setActiveDropdown(null)}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-semibold text-sm text-neutral-900 group-hover/item:text-emerald-700 transition-colors whitespace-nowrap">
                                                                        {subItem.label}
                                                                    </span>
                                                                    {subItem.badge && <Badge label={subItem.badge} />}
                                                                </div>
                                                                <p className="mt-0.5 text-xs text-neutral-500 leading-snug">{subItem.description}</p>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}

                        {/* 互動遊戲：次要入口，維持可見但不搶主導覽的視覺權重 */}
                        <Link
                            to={ROUTES.GAME}
                            className="ml-2 shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-emerald-700 bg-emerald-50/80 ring-1 ring-emerald-200/70 hover:bg-emerald-100 hover:ring-emerald-300 transition-colors whitespace-nowrap"
                        >
                            <GameIcon className="w-4 h-4" />
                            匹克球遊戲
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="xl:hidden p-2.5 text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all duration-200"
                        aria-label="切換選單"
                    >
                        {mobileMenuOpen ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="xl:hidden mt-5 pb-4 space-y-1.5 border-t border-neutral-200/60 pt-5 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain">
                        {/* 互動遊戲：次要入口，不再用整塊漸層按鈕搶版面 */}
                        <Link
                            to={ROUTES.GAME}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 px-5 py-3 mb-3 rounded-xl font-semibold text-emerald-700 bg-emerald-50/80 ring-1 ring-emerald-200/60"
                        >
                            <GameIcon className="w-4 h-4" />
                            匹克球遊戲
                        </Link>

                        {navigation.map((item, index) => (
                            <div key={index}>
                                {item.type === 'link' ? (
                                    <Link
                                        to={item.path!}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`
                      block px-5 py-3 rounded-xl font-semibold transition-all duration-200
                      ${isActive(item.path!)
                                                ? 'text-emerald-700 bg-emerald-50'
                                                : 'text-neutral-700 hover:bg-neutral-50'
                                            }
                    `}
                                    >
                                        {item.label}
                                    </Link>
                                ) : (
                                    <div className="space-y-1.5">
                                        <div className="px-5 py-2 text-neutral-500 text-xs font-bold uppercase tracking-wider">
                                            {item.label}
                                        </div>
                                        {item.items?.map((subItem, subIndex) => (
                                            <Link
                                                key={subIndex}
                                                to={subItem.path}
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="block px-5 py-3 rounded-xl text-neutral-700 hover:bg-neutral-50 transition-all duration-200 ml-3"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold">{subItem.label}</span>
                                                    {subItem.badge && <Badge label={subItem.badge} />}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
